/**
 * SubscribePublicPage Component
 *
 * Public subscription page that handles both authenticated and unauthenticated users.
 *
 * Flow:
 * - Shows plan selection (PlanSelectionPage)
 * - On continue:
 *   - If NOT logged in → redirect to /register with plan params
 *   - If logged in + has subscription → redirect to /settings
 *   - If logged in + no subscription → initiate Stripe checkout
 *
 * Project: P07 - Stripe Integration
 * Sprint: S05 - Polish & Production Readiness
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { Alert } from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import { PlanSelectionPage } from './PlanSelector';
import { useAuth } from '@/hooks/useAuth';
import { subscriptionsRepository } from '@/repositories/subscriptionsRepository';
import type { PlanType, BillingInterval } from '@enfomentis/shared/types';

export function SubscribePublicPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { authState } = useAuth();

  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle plan selection and continue
  const handleContinue = async (
    planType: PlanType,
    billingInterval: BillingInterval
  ) => {
    setError(null);

    // If auth is still loading, wait
    if (authState.isLoading) {
      return;
    }

    // If user is not logged in, redirect to register page
    if (!authState.user) {
      const params = new URLSearchParams({
        plan: planType,
        interval: billingInterval,
      });
      navigate(`/register?${params.toString()}`);
      return;
    }

    // User is logged in - check if they already have a subscription
    setIsCheckoutLoading(true);

    try {
      const hasSubscription = await subscriptionsRepository.hasActiveSubscription(
        authState.user.id
      );

      if (hasSubscription) {
        // User already has subscription, redirect to settings
        navigate('/settings', { replace: true });
        return;
      }

      // User doesn't have subscription - create checkout session
      const response = await fetch(
        `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authState.sessionToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            planType,
            billingInterval,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        if (result.code === 'ALREADY_SUBSCRIBED') {
          navigate('/settings', { replace: true });
          return;
        }
        setError(result.error || 'Failed to create checkout session.');
        setIsCheckoutLoading(false);
        return;
      }

      // Redirect to Stripe Checkout
      window.location.href = result.checkoutUrl;
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to initiate checkout. Please try again.');
      setIsCheckoutLoading(false);
    }
  };

  // Loading overlay during checkout redirect
  if (isCheckoutLoading) {
    return (
      <AppLayout hideFooter>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
            <p className="text-lg">Redirecting to checkout...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout hideFooter>
      {/* Error Alert */}
      {error && (
        <div className="max-w-5xl mx-auto px-4 pt-8">
          <Alert hideIcon color="danger" variant="flat" description={error} />
        </div>
      )}

      {/* Plan Selection Page */}
      <PlanSelectionPage onContinue={handleContinue} />
    </AppLayout>
  );
}
